/**
 * Loads a Contentful `page` entry, with its sections, for the CMS-built pages.
 *
 * Used at build by `pages/[...path].tsx` → `getStaticProps`, and in the browser by
 * `ContentfulPagePreview.tsx` for draft previews. The field lists come from the components that
 * render them (`ContentfulPage.tsx` → `ContentfulPageFields` and the two section layouts), so a
 * field a component starts reading must be added to its list too, or `Crawl` prunes it away.
 *
 * Related: `@ryerson/frontend.contentful` → `Crawl`, which fetches every locale, pages through
 * results 100 at a time, tries each request three times, 6 s apart, then throws.
 */
import { Crawl } from "@ryerson/frontend.contentful";
import { ContentfulPageFields, IContentfulPage } from "./ContentfulPage";
import { LanguageContent } from "@ryerson/frontend.common";
import { ContentfulSectionLayout1Fields } from "./ContentfulSectionLayout1";
import { ContentfulSectionLayout2Fields } from "./ContentfulSectionLayout2";

/** The `type` values that `ContentSection` and `Typography` accept, as the sections use them. */
export type GenericType = "primary" | "secondary" | "tertiary";

/**
 * Lower-cases a Contentful section `type` to a {@link GenericType}. It doesn't check the value:
 * anything else is passed through as it is.
 */
const getType = (type: string): GenericType => {
	return type.toLowerCase() as GenericType;
};

/**
 * Fetches the `page` entry whose `path` equals `path` exactly (for example `/a/b`, no trailing
 * slash), in English, Spanish and French, with rich text resolved.
 *
 * Returns `null` when no entry matches. It doesn't filter by company. `Crawl.get` returns a single
 * match as the entry itself, but two or more matches as an array, which `ContentfulPage.tsx` can't
 * render. Throws when Contentful fails three times, which at build fails the build
 * (web-architecture ADR-0033).
 *
 * `crawlOptions` overrides the Contentful space, environment and token; omitted, `Crawl` reads
 * `CONTENTFUL_SPACE_ID`, `CONTENTFUL_ENVIRONMENT` and `CONTENTFUL_ACCESS_TOKEN`. `preview: true`
 * reads drafts from the Preview API, which needs a Preview token.
 */
const getPageContent = async (
	path: string,
	crawlOptions?: {
		spaceId?: string;
		environmentId?: string;
		accessToken?: string;
		preview?: boolean;
	}
) => {
	let pageContent: LanguageContent<IContentfulPage> | null = (await new Crawl(crawlOptions).get(
		"page",
		{
			filters: {
				"fields.path": path,
			},
			resolveRichText: true,
			depth: 4,
			include: [
				...ContentfulPageFields,
				...ContentfulSectionLayout1Fields,
				...ContentfulSectionLayout2Fields,
			],
		}
	)) as LanguageContent<IContentfulPage>;
	if (Array.isArray(pageContent.en) && pageContent.en.length === 0) {
		pageContent = null;
	}
	return pageContent;
};

/** The helpers above, also the default export. */
export const ContentfulUtils = {
	getType,
	getPageContent,
};

export default ContentfulUtils;
